import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminPageShell from '../admin/AdminPageShell';
import { ADMIN_HERO_BACKGROUND } from '../admin/AdminPageHero';
import AdminPagination from '../admin/AdminPagination';
import ApprenantPageShell from '../apprenant/ApprenantPageShell';
import FormateurPageShell from '../formateur/FormateurPageShell';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  getNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  trackNotificationInteraction,
} from '../../services/api';
import { resolveNotificationLink } from '../../utils/notificationPortal';
import type { NotificationPortal } from '../../utils/notificationPortal';
import {
  syncUnreadCountAfterRead,
  useNotificationUnread,
} from '../../contexts/NotificationUnreadContext';

type NotificationRow = {
  id: string;
  type?: string;
  title?: string;
  message?: string;
  status?: string;
  dateCreation?: string;
  deepLink?: string | null;
  payload?: Record<string, unknown> | null;
};

type StatusFilter = 'ALL' | 'READ' | 'UNREAD';

const PAGE_SIZE = 12;

const PORTAL_COPY: Record<
  NotificationPortal,
  {
    description: string;
    inboxSubtitle: string;
    sideLinks: Array<{ label: string; to: string; icon: string }>;
  }
> = {
  apprenant: {
    description:
      "Alertes académiques, résultats, convocations et annonces de l'administration. Les mêmes messages sont aussi envoyés par e-mail et notification push.",
    inboxSubtitle: 'Rejet de dossier, convocations, planning et résultats de délibération',
    sideLinks: [
      { label: 'Mes cours', to: '/app/mes-cours', icon: 'list_alt' },
      { label: 'Mon dossier', to: '/app/documents', icon: 'folder' },
    ],
  },
  formateur: {
    description:
      'Convocations, mises à jour de planning, annonces administration et alertes liées à vos sessions.',
    inboxSubtitle: 'Planning, convocations et communications institutionnelles',
    sideLinks: [
      { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
      { label: 'Accueil', to: '/formateur', icon: 'dashboard' },
    ],
  },
  admin: {
    description:
      'Alertes opérationnelles, diffusions et notifications liées à la gestion académique.',
    inboxSubtitle: 'Activité récente et annonces système',
    sideLinks: [
      { label: 'Communication', to: '/admin/communication', icon: 'notifications_active' },
      { label: 'Tableau de bord', to: '/admin/dashboard', icon: 'dashboard' },
    ],
  },
  partenaire: {
    description: 'Notifications parrainage, portefeuille et annonces institutionnelles.',
    inboxSubtitle: 'Commissions, parrainage et alertes partenaire',
    sideLinks: [
      { label: 'Parrainage', to: '/partenaire/parrainage', icon: 'groups' },
      { label: 'Portefeuille', to: '/partenaire/wallet', icon: 'account_balance_wallet' },
    ],
  },
};

function formatShortDate(iso?: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function notificationTypeLabel(type?: string) {
  const t = (type || '').toUpperCase();
  if (t.includes('ACADEMIC_RESULTS')) return 'Résultats';
  if (t.includes('DOCUMENT_PENDING')) return 'Dossier à valider';
  if (t.includes('DOCUMENT_VALIDATED')) return 'Dossier validé';
  if (t.includes('DOCUMENT_REJECTED')) return 'Dossier refusé';
  if (t.includes('ADMIN_ACADEMIC')) return 'Annonce administration';
  if (t.includes('ACADEMIC_ALERT')) return 'Alerte académique';
  if (t.includes('PAY') || t.includes('WALLET') || t.includes('FLEX')) return 'Paiement';
  if (t.includes('WITHDRAWAL')) return 'Retrait portefeuille';
  if (t.includes('SECUR') || t.includes('AUTH')) return 'Sécurité';
  if (t.includes('REFERRAL')) return 'Parrainage';
  if (t.includes('STAGE')) return 'Stage';
  return type || 'Notification';
}

function notificationIcon(type?: string): string {
  const t = (type || '').toUpperCase();
  if (t.includes('ACADEMIC_RESULTS')) return 'emoji_events';
  if (t.includes('DOCUMENT_PENDING')) return 'folder_shared';
  if (t.includes('DOCUMENT_VALIDATED')) return 'check_circle';
  if (t.includes('DOCUMENT_REJECTED')) return 'cancel';
  if (t.includes('ADMIN_ACADEMIC') || t.includes('ACADEMIC_ALERT')) return 'campaign';
  if (t.includes('PAY') || t.includes('WALLET')) return 'account_balance_wallet';
  if (t.includes('WITHDRAWAL')) return 'payments';
  if (t.includes('SECUR') || t.includes('AUTH')) return 'shield';
  if (t.includes('REFERRAL')) return 'groups';
  if (t.includes('STAGE')) return 'work';
  return 'notifications';
}

function PortalShell({
  portal,
  children,
  ...props
}: {
  portal: NotificationPortal;
  title: string;
  description: string;
  sideCard: {
    label: string;
    value: string;
    helper: string;
    sideLinks: Array<{ label: string; to: string; icon: string }>;
  };
  onRetry: () => void;
  isLoading: boolean;
  isError: boolean;
  loadingMessage: string;
  errorMessage: string;
  primaryAction?: ReactNode;
  children: ReactNode;
}) {
  const shellProps = {
    title: props.title,
    description: props.description,
    sideCard: props.sideCard,
    onRetry: props.onRetry,
    isLoading: props.isLoading,
    isError: props.isError,
    loadingMessage: props.loadingMessage,
    errorMessage: props.errorMessage,
    primaryAction: props.primaryAction,
    children,
  };

  if (portal === 'apprenant') return <ApprenantPageShell {...shellProps} />;
  if (portal === 'formateur') return <FormateurPageShell {...shellProps} />;
  return (
    <AdminPageShell
      {...shellProps}
      backgroundImage={portal === 'admin' ? ADMIN_HERO_BACKGROUND : undefined}
    />
  );
}

export default function PortalNotificationsPanel({ portal }: { portal: NotificationPortal }) {
  const navigate = useNavigate();
  const { unreadCount, refreshUnread, setUnreadCount, decrementUnread, clearUnread } =
    useNotificationUnread();
  const copy = PORTAL_COPY[portal];
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [typeFilter, setTypeFilter] = useState('');
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await getNotifications({
        page,
        limit: PAGE_SIZE,
        status: statusFilter === 'READ' ? 'READ' : undefined,
        type: typeFilter.trim() || undefined,
      });
      const rows = (result.items || []) as NotificationRow[];
      const filtered =
        statusFilter === 'UNREAD' ? rows.filter((item) => item.status !== 'READ') : rows;
      setItems(filtered);
      setUnreadCount(result.unreadCount || 0);
      setTotalPages(Math.max(1, result?.pagination?.totalPages || 1));
      setTotalItems(Math.max(0, Number(result?.pagination?.total) || filtered.length));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, setUnreadCount]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const statsLabel = useMemo(() => {
    if (unreadCount > 0) {
      return `${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`;
    }
    return 'Toutes vos alertes sont lues';
  }, [unreadCount]);

  const paginationSummary = useMemo(() => {
    if (totalItems <= 0) return 'Aucune notification';
    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(page * PAGE_SIZE, totalItems);
    return `Affichage ${rangeStart}–${rangeEnd} sur ${totalItems} notification${totalItems > 1 ? 's' : ''}`;
  }, [page, totalItems]);

  const handleMarkRead = async (id: string) => {
    setMarkingId(id);
    decrementUnread();
    try {
      const result = await markNotificationAsRead(id);
      await trackNotificationInteraction(id, 'CLICK', 'web');
      await syncUnreadCountAfterRead({ setUnreadCount, refreshUnread }, result?.unreadCount);
      await load();
    } catch {
      await refreshUnread();
    } finally {
      setMarkingId(null);
    }
  };

  const handleViewNotification = async (item: NotificationRow) => {
    const actionLink = resolveNotificationLink(item, portal);
    const wasUnread = item.status !== 'READ';
    setMarkingId(item.id);
    if (wasUnread) {
      decrementUnread();
    }
    try {
      if (wasUnread) {
        const result = await markNotificationAsRead(item.id);
        await trackNotificationInteraction(item.id, 'CLICK', 'web');
        await syncUnreadCountAfterRead({ setUnreadCount, refreshUnread }, result?.unreadCount);
      }
      if (actionLink) {
        navigate(actionLink);
      }
      await load();
    } catch {
      if (wasUnread) {
        await refreshUnread();
      }
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    clearUnread();
    try {
      const result = await markAllNotificationsAsRead();
      await syncUnreadCountAfterRead({ setUnreadCount, refreshUnread }, result?.unreadCount);
      await load();
    } catch {
      await refreshUnread();
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <PortalShell
      portal={portal}
      title="Notifications"
      description={copy.description}
      sideCard={{
        label: 'Non lues',
        value: loading ? '…' : String(unreadCount),
        helper: statsLabel,
        sideLinks: copy.sideLinks,
      }}
      onRetry={() => void load()}
      isLoading={loading}
      isError={error}
      loadingMessage="Chargement de vos notifications…"
      errorMessage="Impossible de charger vos notifications."
      primaryAction={
        unreadCount > 0 && !loading && !error ? (
          <button
            type="button"
            disabled={markingAll}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-xs font-bold text-primary disabled:opacity-50"
            onClick={() => void handleMarkAllRead()}
          >
            <LucideSymbolIcon name="checklist" tone="inherit" size={16} />
            {markingAll ? 'Mise à jour…' : 'Tout marquer comme lu'}
          </button>
        ) : undefined
      }
    >
      <section className="overflow-hidden rounded-2xl border border-outline-variant/20 bg-white shadow-sm">
        <header className="border-b border-outline-variant/15 px-4 py-4 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <LucideSymbolIcon name="notifications" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-extrabold text-slate-900">Boîte de réception</h2>
                <p className="mt-0.5 text-sm text-slate-500">{copy.inboxSubtitle}</p>
              </div>
            </div>
            {unreadCount > 0 ? (
              <button
                type="button"
                disabled={markingAll}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                onClick={() => void handleMarkAllRead()}
              >
                {markingAll ? '…' : 'Tout lire'}
              </button>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {(['ALL', 'UNREAD', 'READ'] as const).map((filter) => (
              <button
                key={filter}
                type="button"
                className={[
                  'rounded-full border px-3 py-1 text-xs font-bold transition-colors',
                  statusFilter === filter
                    ? 'border-primary/60 bg-primary/10 text-primary'
                    : 'border-outline-variant/30 text-slate-500 hover:bg-slate-50',
                ].join(' ')}
                onClick={() => {
                  setPage(1);
                  setStatusFilter(filter);
                }}
              >
                {filter === 'ALL' ? 'Toutes' : filter === 'UNREAD' ? 'Non lues' : 'Lues'}
              </button>
            ))}
            <input
              type="text"
              value={typeFilter}
              onChange={(event) => {
                setPage(1);
                setTypeFilter(event.target.value);
              }}
              placeholder="Filtrer par type (ex. ACADEMIC_ALERT)"
              className="min-w-[12rem] flex-1 rounded-lg border border-outline-variant/20 bg-slate-50 px-3 py-1.5 text-xs text-slate-700"
            />
          </div>
        </header>

        <div className="divide-y divide-outline-variant/10">
          {!loading && !error && items.length === 0 ? (
            <div className="px-4 py-12 text-center sm:px-5">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <LucideSymbolIcon name="notifications_active" tone="inherit" size={28} />
              </span>
              <p className="mt-4 text-sm font-semibold text-slate-700">Aucune notification</p>
              <p className="mt-1 text-xs text-slate-500">Vos alertes et annonces apparaîtront ici.</p>
            </div>
          ) : null}

          {items.map((item) => {
            const isRead = item.status === 'READ';
            const actionLink = resolveNotificationLink(item, portal);
            return (
              <article
                key={item.id}
                className={['px-4 py-4 sm:px-5', isRead ? 'bg-white' : 'bg-primary/[0.04]'].join(' ')}
              >
                <div className="flex gap-3">
                  <span
                    className={[
                      'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                      isRead ? 'bg-slate-100 text-slate-500' : 'bg-primary/10 text-primary',
                    ].join(' ')}
                  >
                    <LucideSymbolIcon name={notificationIcon(item.type)} tone="inherit" size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="text-sm font-bold text-slate-900">{item.title || 'Notification'}</p>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        {notificationTypeLabel(item.type)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{item.message || ''}</p>
                    <p className="mt-2 text-[11px] font-medium text-slate-400">
                      {formatShortDate(item.dateCreation)}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      {actionLink ? (
                        <button
                          type="button"
                          disabled={markingId === item.id}
                          className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
                          onClick={() => void handleViewNotification(item)}
                        >
                          {markingId === item.id ? '…' : 'Voir'}
                        </button>
                      ) : !isRead ? (
                        <button
                          type="button"
                          disabled={markingId === item.id}
                          className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
                          onClick={() => void handleMarkRead(item.id)}
                        >
                          {markingId === item.id ? '…' : 'Marquer comme lu'}
                        </button>
                      ) : null}
                      {isRead ? (
                        <span className="text-xs font-semibold text-emerald-700">Lu</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {!loading && !error && totalItems > 0 ? (
          <div className="border-t border-outline-variant/15 px-4 py-3 sm:px-5">
            <AdminPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              summary={paginationSummary}
              showFirstLast={portal === 'admin'}
            />
          </div>
        ) : null}
      </section>
    </PortalShell>
  );
}
